/**
 * User display-name resolution.
 *
 * One place for the `display_name || humanizeEmail(email)` resolution that was
 * inline-duplicated across the task/project member surfaces. Pure + unit-tested:
 *  - `memberLabel`  → tooltip-less contexts (dropdown/filter labels): keeps the raw
 *                     email (no lossy guess), uid as last resort.
 *  - `memberName`   → name-only (no uid): the presentational `UserName.vue` renders
 *                     a placeholder instead of ever showing a raw uid.
  *  - `userInitials` → the avatar's letters: the given name when there is one, else the email.
  *  - `resolveUserTooltip` → the member's email (never the uid) to show on hover.
 */

/** Minimal shape needed to resolve a display name (a `Member` satisfies it). */
export interface UserLike {
  uid?: string | null
  email?: string | null
  display_name?: string | null
}

/**
 * Best-effort human name from an email local-part. Heuristic and **lossy** — opaque
 * locals (`jdoe`, initials, all-numeric) come out poorly, so this is only a *fallback*
 * after `display_name`, and callers should keep the real email visible.
 *
 * Rules: take the local part, drop a `+tag`, drop digits, split on `. _ -`, Title-Case.
 * e.g. `jane.doe@x.io → "Jane Doe"`, `jane.doe+work@x.io → "Jane Doe"`, `12345@x.io → ""`.
 */
export function humanizeEmail(email: string | null | undefined): string {
  if (!email) return ''
  const local = (email.split('@')[0] ?? '') // local part
    .split('+')[0] // drop +tag
    .replace(/[0-9]/g, '') // drop digits
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Human name for a user **without any uid fallback**: `display_name`, else a
 * humanized email, else `''`. For UI that must never render a raw uid — the caller
 * (see `UserName.vue`) substitutes its own placeholder when this returns empty.
 */
export function memberName(user: UserLike | null | undefined): string {
  if (!user) return ''
  return user.display_name || humanizeEmail(user.email) || ''
}

/**
 * Label for a **tooltip-less** context: `display_name`, else the **raw** email,
 * else the uid. No humanization, so the real address is never replaced by a
 * lossy guess a user can't disambiguate (e.g. digits stripped). Returns `''`
 * when nothing is available.
 */
export function memberLabel(user: UserLike | null | undefined): string {
  if (!user) return ''
  return user.display_name || user.email || user.uid || ''
}

/** A member picker option: a humanized `name` line + the raw `email` line. */
export interface MemberOption {
  value: string
  name: string
  email: string
  /** Combined "name email" for the Select's type-ahead / filtering. */
  label: string
}

/**
 * Build a two-line picker option for a member: the humanized `name` (or
 * `fallbackName` when there's no name/email) plus the raw `email` beneath it, so
 * homonyms (e.g. `a@x` vs `a+02@x`, which humanize to the same name) stay
 * distinguishable in a selector. See `MemberOptionLabel.vue`.
 */
export function memberOption(user: UserLike, fallbackName: string): MemberOption {
  const name = memberName(user) || fallbackName
  const email = user.email ?? ''
  return { value: user.uid ?? '', name, email, label: `${name} ${email}`.trim() }
}

/**
 * The email to surface as a tooltip alongside a resolved name — so a humanized
 * guess isn't mistaken for fact. Never the uid: when there's no email the tooltip
 * is empty (a raw uid must never appear in the UI; it goes to the dev log instead).
 */
export function resolveUserTooltip(uid: string, members: readonly UserLike[]): string {
  const member = members.find((m) => m.uid === uid)
  return member?.email || ''
}

/**
 * The letters of a user's avatar (MYAC-1522). The name the person gave wins: the first
 * letter of the first name, then of the last name when there is one ("Jane Doe" → "JD",
 * "Jane" → "J"). Without a first name it falls back to the email's local part, as before:
 * a first letter on each side of a `. _ -` separator, else the first two letters; `?` when
 * there is nothing at all.
 */
export function userInitials(user: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}): string {
  const firstLetter = (value: string | null | undefined) => [...(value?.trim() ?? '')][0] ?? ''
  const first = firstLetter(user.first_name)
  if (first) return (first + firstLetter(user.last_name)).toLocaleUpperCase()

  const email = user.email
  if (!email) return '?'
  const namePart = email.split('@')[0]
  const parts = namePart.split(/[._-]/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return namePart.substring(0, 2).toUpperCase()
}
